import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import '../../Style/EditModal.scss';
import "../../Style/TableStyle.scss"
import '../../Style/__searchbar.scss'
import '../../Style/__button.scss'
import './KartelaArtikullitPage.scss';
import "../AccountingReportsCRUD/AccountsReportPage.scss";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FaChartLine } from "react-icons/fa";
import { AiOutlineFilePdf, AiOutlinePrinter } from "react-icons/ai";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { errorAlert } from "../../Components/Alerts";
import { AddActionsButton } from "../../Components/AddElementsButton";
import { AiOutlineClose } from "react-icons/ai";
import ExcelFormat from "../../Components/ExcelConverter/ExcelFormat";
import { ArticleCardGridHeader, ArticleDetailsColumns, ArticleDetailsGrid, ArticleDetailsMappings, sinteticArticleColumns, sinteticArticleMappings } from "./GridHeaders/ArticleCardGrid";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ReusableAccountBooksGrid from "../../Components/ReusableAccountBooksGrid";
import CrudPageHeader from "../../Components/CrudPageHeader";

const ARTICLE_CARD_ENDPOINTS = {
    syntheticV2: 'Card/articleSyntheticAnalysisV2',
    detailsV2: 'Card/articleDetailsV2',
    detailsChartV2: 'Card/articleDetailsChartV2',
    syntheticLegacy: 'Card/articleSyntheticAnalysis',
    detailsLegacy: 'Card/articleDetails',
    detailsChartLegacy: 'Card/articleDetailsChart',
};

export default function KartelaArtikullitPage() {
    const [data, setData] = useState([]);
    const [mainLoading, setMainLoading] = useState(false);
    const [mainRowCount, setMainRowCount] = useState(0);
    const [mainTotals, setMainTotals] = useState({});
    const headers = authHeader();
    const { fiscalYear } = useContext(FiscalYearContext);
    const [articleDetails, setArticleDetails] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsRowCount, setDetailsRowCount] = useState(0);
    const [detailsTotals, setDetailsTotals] = useState({});
    const [selectedArticleId, setSelectedArticleId] = useState(null);

    const [showallAccounts, setShowAllAccounts] = useState(true);
    const [showDetailAccounts, setShowDetailsAccounts] = useState(false);

    const [articleName, setArticleName] = useState("");

    const [articleDetailsChart, setArticleDetailsChart] = useState([]);
    const [mainPaginationModel, setMainPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [mainSortModel, setMainSortModel] = useState([]);
    const [mainFilterModel, setMainFilterModel] = useState({ items: [] });
    const [mainQuickFilter, setMainQuickFilter] = useState([]);
    const [detailsPaginationModel, setDetailsPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [detailsSortModel, setDetailsSortModel] = useState([]);
    const [detailsFilterModel, setDetailsFilterModel] = useState({ items: [] });
    const [detailsQuickFilter, setDetailsQuickFilter] = useState([]);
    const [companiesData, setCompaniesData] = useState([]);
    const [logoPath, setLogoPath] = useState("");

    const requestWithFallback = async (v2Endpoint, legacyEndpoint, params) => {
        try {
            return await axios.get(ApiLink.API_URL + v2Endpoint, { headers, params });
        } catch (error) {
            if (error?.response?.status !== 404) {
                throw error;
            }

            return axios.get(ApiLink.API_URL + legacyEndpoint, { headers, params });
        }
    };

    const buildGridParams = (paginationModel, sortModel, filterModel, quickFilter, extra = {}) => ({
        fiscalYear,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sort: JSON.stringify(sortModel || []),
        filter: JSON.stringify(filterModel?.items || []),
        quickFilter: (quickFilter || []).join(" "),
        ...extra,
    });

    const normalizePagedPayload = (payload) => {
        if (Array.isArray(payload)) {
            return {
                items: payload,
                total: payload.length,
                totals: null,
            };
        }

        return {
            items: Array.isArray(payload?.items) ? payload.items : Array.isArray(payload?.Items) ? payload.Items : [],
            total: Number(payload?.total ?? payload?.Total ?? 0),
            totals: payload?.totals || payload?.Totals || null,
        };
    };

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers });
            const companyItems = Array.isArray(response?.data) ? response.data : [];
            setCompaniesData(companyItems);
            setLogoPath(companyItems[0]?.CompanyLogo ? ApiLink.PHOTO_URL + companyItems[0].CompanyLogo : "");
        } catch (error) {
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave të kompanisë: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const fetchMaterialSintetikData = async () => {
        setMainLoading(true);
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.syntheticV2,
                ARTICLE_CARD_ENDPOINTS.syntheticLegacy,
                buildGridParams(mainPaginationModel, mainSortModel, mainFilterModel, mainQuickFilter)
            );

            const normalized = normalizePagedPayload(response.data);
            setData(normalized.items);
            setMainRowCount(Number(normalized.total ?? normalized.items.length));
            setMainTotals(normalized.totals || {});
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response?.data?.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë.");
            }
        } finally {
            setMainLoading(false);
        }
    };

    const rows = useMemo(() => data.map((item, index) => ({
        id: index + 1,
        rowNumber: mainPaginationModel.page * mainPaginationModel.pageSize + index + 1,
        barcode: item.Barcode,
        articleName: item.ArticleName,
        jan: item.Janar,
        feb: item.Shkurt,
        mar: item.Mars,
        apr: item.Prill,
        may: item.Maj,
        jun: item.Qershor,
        jul: item.Korrik,
        aug: item.Gusht,
        sep: item.Shtator,
        oct: item.Tetor,
        nov: item.Nentor,
        dec: item.Dhjetor,
        tot: item.Total,
        avg: item.CmimiMesatar,
        valEu: item.VleraNeEuro,

        articleID: item.ArticleID,
        rowInfo: item,
    })), [data, mainPaginationModel.page, mainPaginationModel.pageSize]);

    const detailsRows = useMemo(() => articleDetails.map((item, index) => ({
        id: index + 1,
        rowNumber: detailsPaginationModel.page * detailsPaginationModel.pageSize + index + 1,
        barcode: item.Barcode,
        articleName: item.ArticleName,
        accountName: item.AccountName,
        jan: item.Janar,
        feb: item.Shkurt,
        mar: item.Mars,
        apr: item.Prill,
        may: item.Maj,
        jun: item.Qershor,
        jul: item.Korrik,
        aug: item.Gusht,
        sep: item.Shtator,
        oct: item.Tetor,
        nov: item.Nentor,
        dec: item.Dhjetor,
        tot: item.Total,
        avg: item.CmimiMesatar,
        valEu: item.VleraNeEuro,
    })), [articleDetails, detailsPaginationModel.page, detailsPaginationModel.pageSize]);

    const currencyFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }), []);

    const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

    const reportPeriodLabel = useMemo(() => `${fiscalYear}`, [fiscalYear]);

    const sanitizeFileName = (value, fallback) => {
        const cleaned = String(value || "")
            .trim()
            .replace(/[\\/:*?"<>|]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_|_$/g, "");

        return cleaned || fallback;
    };

    const formatDateForDisplay = (date) => {
        if (!date) {
            return "";
        }

        const parsedDate = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(parsedDate.getTime())) {
            return String(date);
        }

        const day = String(parsedDate.getDate()).padStart(2, "0");
        const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
        const year = parsedDate.getFullYear();
        return `${day}.${month}.${year}`;
    };

    const addLogo = (doc) => {
        if (!logoPath) {
            return;
        }

        try {
            doc.addImage(logoPath, "JPEG", 12, 10, 24, 24);
        } catch (error) {
            try {
                doc.addImage(logoPath, "PNG", 12, 10, 24, 24);
            } catch (logoError) {
                // Logo is optional; keep the report usable if the image cannot be embedded.
            }
        }
    };

    const addReportHeader = (doc, title, subtitle, periodLabel) => {
        const company = companiesData[0];
        const pageWidth = doc.internal.pageSize.getWidth();

        addLogo(doc);

        doc.setDrawColor(40, 73, 69);
        doc.setLineWidth(0.5);
        doc.line(12, 38, pageWidth - 12, 38);

        doc.setTextColor(31, 58, 54);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(title, 40, 18);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(95, 127, 115);
        doc.text(subtitle, 40, 24);

        doc.setFontSize(9);
        doc.setTextColor(31, 58, 54);
        doc.text(`Periudha: ${periodLabel}`, 12, 46);

        if (!company) {
            return 52;
        }

        const companyDetails = [
            company.CompanyName,
            `Numri Identifikues: ${company.IdentificationNumber || "-"}`,
            `Numri i TVSH-së: ${company.VATNumber || "-"}`,
            `Adresa: ${company.Address || "-"}`,
            `Tel: ${company.PhoneNumbers || "-"}`,
            `Email: ${company.Email || "-"}`,
        ];

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(31, 58, 54);
        doc.text(companyDetails[0], pageWidth - 12, 16, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(95, 127, 115);
        doc.text(companyDetails.slice(1), pageWidth - 12, 22, { align: "right" });

        return 52;
    };

    const addReportFooter = (doc, periodLabel, generatedOnLabel) => {
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brandingLabel = "© www.accountree.net";
        const footerCenterLabel = `Gjeneruar më: ${generatedOnLabel} | ${brandingLabel}`;

        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setDrawColor(212, 226, 219);
            doc.setLineWidth(0.3);
            doc.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(95, 127, 115);
            doc.text(`Periudha: ${periodLabel}`, 12, pageHeight - 5.5);
            doc.text(footerCenterLabel, pageWidth / 2, pageHeight - 5.5, { align: "center" });
            doc.text(`Faqe ${page} / ${pageCount}`, pageWidth - 12, pageHeight - 5.5, { align: "right" });
        }
    };

    const addPdfTable = (doc, headersRow, bodyRows, footRow, startY, baseColumnStyles) => {
        const horizontalMargin = 12;
        const availableTableWidth = doc.internal.pageSize.getWidth() - (horizontalMargin * 2);
        const baseTableWidth = Object.values(baseColumnStyles).reduce(
            (sum, style) => sum + Number(style.cellWidth || 0),
            0,
        );
        const scaleRatio = baseTableWidth > 0 ? availableTableWidth / baseTableWidth : 1;
        const columnStyles = Object.entries(baseColumnStyles).reduce((accumulator, [key, style]) => {
            accumulator[key] = {
                ...style,
                cellWidth: Number((Number(style.cellWidth || 0) * scaleRatio).toFixed(3)),
            };
            return accumulator;
        }, {});

        doc.autoTable({
            head: [headersRow],
            body: bodyRows,
            foot: [footRow],
            startY,
            showHead: "firstPage",
            showFoot: "lastPage",
            margin: {
                top: 16,
                left: horizontalMargin,
                right: horizontalMargin,
                bottom: 16,
            },
            theme: "grid",
            styles: {
                fontSize: 6.5,
                cellPadding: { top: 1.3, right: 1.2, bottom: 1.3, left: 1.2 },
                lineColor: [212, 226, 219],
                lineWidth: 0.1,
                textColor: [31, 58, 54],
                overflow: "linebreak",
            },
            headStyles: {
                fillColor: [40, 73, 69],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
            },
            bodyStyles: {
                valign: "middle",
            },
            footStyles: {
                fillColor: [231, 240, 235],
                textColor: [31, 58, 54],
                fontStyle: "bold",
            },
            alternateRowStyles: {
                fillColor: [247, 250, 248],
            },
            didParseCell: (hookData) => {
                if (hookData.section !== "foot") {
                    return;
                }

                if (hookData.column.index >= 3) {
                    hookData.cell.styles.halign = "right";
                }
            },
            columnStyles,
        });
    };

    const monthPdfHeaders = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"];
    const monthDataKeys = ["Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor", "Korrik", "Gusht", "Shtator", "Tetor", "Nentor", "Dhjetor"];

    const buildCardPdfTableData = (items, mode) => {
        const totalsByMonth = monthDataKeys.reduce((accumulator, key) => ({ ...accumulator, [key]: 0 }), {});
        let total = 0;
        let valueInEuro = 0;

        const bodyRows = items.map((item, index) => {
            monthDataKeys.forEach((key) => {
                totalsByMonth[key] += Number(item[key] || 0);
            });
            total += Number(item.Total || 0);
            valueInEuro += Number(item.VleraNeEuro || 0);

            const row = [
                String(index + 1),
                item.Barcode || "",
                item.ArticleName || "",
            ];

            if (mode === "details") {
                row.push(item.AccountName || "");
            }

            return [
                ...row,
                ...monthDataKeys.map((key) => formatCurrency(item[key])),
                formatCurrency(item.Total),
                formatCurrency(item.CmimiMesatar),
                formatCurrency(item.VleraNeEuro),
            ];
        });

        const footRow = [
            "",
            "",
            "Totali",
        ];

        if (mode === "details") {
            footRow.push("");
        }

        footRow.push(
            ...monthDataKeys.map((key) => formatCurrency(totalsByMonth[key])),
            formatCurrency(total),
            "",
            formatCurrency(valueInEuro),
        );

        return { bodyRows, footRow };
    };

    const buildCardPdfDocument = async (mode) => {
        const isDetails = mode === "details";
        const items = isDetails ? await fetchAllDetailRows() : await fetchAllSyntheticRows();

        if (!items.length) {
            errorAlert("Nuk ka të dhëna për eksport.");
            return null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const generatedOnLabel = formatDateForDisplay(new Date());
        const selectedArticleLabel = isDetails && articleName ? `Artikulli: ${articleName}` : "";
        const startY = addReportHeader(
            doc,
            isDetails ? `Historiku i artikullit${articleName ? `: ${articleName}` : ""}` : "Kartela sintetike e artikujve",
            isDetails
                ? `Raport analitik i detajeve sipas klientit${selectedArticleLabel ? ` | ${selectedArticleLabel}` : ""}`
                : "Raport sintetik i lëvizjeve mujore sipas artikullit",
            reportPeriodLabel,
        );
        const { bodyRows, footRow } = buildCardPdfTableData(items, mode);

        const headersRow = [
            "#",
            "Barkodi",
            "Emërtimi artikullit",
            ...(isDetails ? ["Klienti"] : []),
            ...monthPdfHeaders,
            "Total",
            "Çmimi mesatar",
            "Vlera në euro",
        ];

        const firstValueColumnIndex = isDetails ? 4 : 3;
        const baseColumnStyles = {
            0: { cellWidth: 8, halign: "center" },
            1: { cellWidth: 20 },
            2: { cellWidth: isDetails ? 30 : 34 },
            ...(isDetails ? { 3: { cellWidth: 28 } } : {}),
        };

        monthDataKeys.forEach((_, index) => {
            baseColumnStyles[firstValueColumnIndex + index] = { cellWidth: 13, halign: "right" };
        });

        baseColumnStyles[firstValueColumnIndex + monthDataKeys.length] = { cellWidth: 15, halign: "right" };
        baseColumnStyles[firstValueColumnIndex + monthDataKeys.length + 1] = { cellWidth: 16, halign: "right" };
        baseColumnStyles[firstValueColumnIndex + monthDataKeys.length + 2] = { cellWidth: 17, halign: "right" };

        addPdfTable(doc, headersRow, bodyRows, footRow, startY, baseColumnStyles);
        addReportFooter(doc, reportPeriodLabel, generatedOnLabel);

        return {
            doc,
            companyNameForFile: sanitizeFileName(companiesData?.[0]?.CompanyName, "Kompania"),
        };
    };

    const printPdfDocument = (doc) => {
        const pdfBlob = doc.output("blob");
        const printUrl = URL.createObjectURL(pdfBlob);
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-10000px";
        printFrame.style.top = "0";
        printFrame.style.width = "1024px";
        printFrame.style.height = "768px";
        printFrame.style.border = "0";
        printFrame.style.opacity = "0";
        printFrame.style.pointerEvents = "none";
        let didPrint = false;
        let cleanupDone = false;

        const cleanup = () => {
            if (cleanupDone) {
                return;
            }

            cleanupDone = true;
            URL.revokeObjectURL(printUrl);
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        };

        printFrame.onload = () => {
            if (didPrint) {
                return;
            }

            didPrint = true;

            try {
                const frameWindow = printFrame.contentWindow;
                if (!frameWindow) {
                    cleanup();
                    errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
                    return;
                }

                window.setTimeout(() => {
                    frameWindow.focus();
                    frameWindow.onafterprint = cleanup;
                    frameWindow.print();
                    window.setTimeout(cleanup, 5000);
                }, 450);
            } catch (error) {
                cleanup();
                errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
            }
        };

        document.body.appendChild(printFrame);
        printFrame.src = printUrl;

        window.setTimeout(() => {
            cleanup();
        }, 60000);
    };

    const exportCardPdf = async (mode) => {
        const generatedDocument = await buildCardPdfDocument(mode);
        if (!generatedDocument) {
            return;
        }

        const { doc, companyNameForFile } = generatedDocument;
        const fileBaseName = mode === "details"
            ? `Historiku_i_artikullit_${sanitizeFileName(articleName, "Artikulli")}`
            : "Kartela_sintetike_e_artikujve";

        doc.save(`${fileBaseName}_${fiscalYear}_${companyNameForFile}.pdf`);
    };

    const printCardPdf = async (mode) => {
        const generatedDocument = await buildCardPdfDocument(mode);
        if (!generatedDocument) {
            return;
        }

        printPdfDocument(generatedDocument.doc);
    };

    const syntheticColumns = useMemo(() => {
        const columnWidthByField = ArticleCardGridHeader.reduce((accumulator, column) => {
            accumulator[column.field] = Number(column.width || column.minWidth || 130);
            return accumulator;
        }, {});

        return [
            { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
            { field: "barcode", headerName: "Barkodi", minWidth: 130, width: columnWidthByField.barcode || 150 },
            { field: "articleName", headerName: "Emertimi artikullit", minWidth: 180, width: columnWidthByField.articleName || 180 },
            { field: "jan", headerName: "Janar", minWidth: 120, width: columnWidthByField.jan || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "feb", headerName: "Shkurt", minWidth: 120, width: columnWidthByField.feb || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "mar", headerName: "Mars", minWidth: 120, width: columnWidthByField.mar || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "apr", headerName: "Prill", minWidth: 120, width: columnWidthByField.apr || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "may", headerName: "Maj", minWidth: 120, width: columnWidthByField.may || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jun", headerName: "Qershor", minWidth: 120, width: columnWidthByField.jun || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jul", headerName: "Korrik", minWidth: 120, width: columnWidthByField.jul || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "aug", headerName: "Gusht", minWidth: 120, width: columnWidthByField.aug || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "sep", headerName: "Shtator", minWidth: 120, width: columnWidthByField.sep || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "oct", headerName: "Tetor", minWidth: 120, width: columnWidthByField.oct || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "nov", headerName: "Nentor", minWidth: 120, width: columnWidthByField.nov || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "dec", headerName: "Dhjetor", minWidth: 120, width: columnWidthByField.dec || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "tot", headerName: "Total", minWidth: 130, width: columnWidthByField.tot || 130, valueFormatter: (value) => formatCurrency(value) },
            { field: "avg", headerName: "Cmimi mesatar", minWidth: 140, width: columnWidthByField.avg || 140, valueFormatter: (value) => formatCurrency(value) },
            { field: "valEu", headerName: "Vlera ne euro", minWidth: 140, width: columnWidthByField.valEu || 140, valueFormatter: (value) => formatCurrency(value) },
        ];
    }, [currencyFormatter]);

    const detailColumns = useMemo(() => {
        const columnWidthByField = ArticleDetailsGrid.reduce((accumulator, column) => {
            accumulator[column.field] = Number(column.width || column.minWidth || 130);
            return accumulator;
        }, {});

        return [
            { field: "rowNumber", headerName: "#", minWidth: 70, width: 80 },
            { field: "barcode", headerName: "Barkodi", minWidth: 130, width: columnWidthByField.barcode || 150 },
            { field: "articleName", headerName: "Emertimi artikullit", minWidth: 180, width: columnWidthByField.articleName || 180 },
            { field: "accountName", headerName: "Klienti", minWidth: 170, width: columnWidthByField.accountName || 170 },
            { field: "jan", headerName: "Janar", minWidth: 120, width: columnWidthByField.jan || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "feb", headerName: "Shkurt", minWidth: 120, width: columnWidthByField.feb || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "mar", headerName: "Mars", minWidth: 120, width: columnWidthByField.mar || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "apr", headerName: "Prill", minWidth: 120, width: columnWidthByField.apr || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "may", headerName: "Maj", minWidth: 120, width: columnWidthByField.may || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jun", headerName: "Qershor", minWidth: 120, width: columnWidthByField.jun || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "jul", headerName: "Korrik", minWidth: 120, width: columnWidthByField.jul || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "aug", headerName: "Gusht", minWidth: 120, width: columnWidthByField.aug || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "sep", headerName: "Shtator", minWidth: 120, width: columnWidthByField.sep || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "oct", headerName: "Tetor", minWidth: 120, width: columnWidthByField.oct || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "nov", headerName: "Nentor", minWidth: 120, width: columnWidthByField.nov || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "dec", headerName: "Dhjetor", minWidth: 120, width: columnWidthByField.dec || 120, valueFormatter: (value) => formatCurrency(value) },
            { field: "tot", headerName: "Total", minWidth: 130, width: columnWidthByField.tot || 130, valueFormatter: (value) => formatCurrency(value) },
            { field: "avg", headerName: "Cmimi mesatar", minWidth: 140, width: columnWidthByField.avg || 140, valueFormatter: (value) => formatCurrency(value) },
            { field: "valEu", headerName: "Vlera ne euro", minWidth: 140, width: columnWidthByField.valEu || 140, valueFormatter: (value) => formatCurrency(value) },
        ];
    }, [currencyFormatter]);

    const summaryFooter = useMemo(() => ({
        labelField: "articleName",
        label: "Totali",
        values: {
            jan: formatCurrency(mainTotals?.janar ?? mainTotals?.Janar ?? 0),
            feb: formatCurrency(mainTotals?.shkurt ?? mainTotals?.Shkurt ?? 0),
            mar: formatCurrency(mainTotals?.mars ?? mainTotals?.Mars ?? 0),
            apr: formatCurrency(mainTotals?.prill ?? mainTotals?.Prill ?? 0),
            may: formatCurrency(mainTotals?.maj ?? mainTotals?.Maj ?? 0),
            jun: formatCurrency(mainTotals?.qershor ?? mainTotals?.Qershor ?? 0),
            jul: formatCurrency(mainTotals?.korrik ?? mainTotals?.Korrik ?? 0),
            aug: formatCurrency(mainTotals?.gusht ?? mainTotals?.Gusht ?? 0),
            sep: formatCurrency(mainTotals?.shtator ?? mainTotals?.Shtator ?? 0),
            oct: formatCurrency(mainTotals?.tetor ?? mainTotals?.Tetor ?? 0),
            nov: formatCurrency(mainTotals?.nentor ?? mainTotals?.Nentor ?? 0),
            dec: formatCurrency(mainTotals?.dhjetor ?? mainTotals?.Dhjetor ?? 0),
            tot: formatCurrency(mainTotals?.total ?? mainTotals?.Total ?? 0),
            avg: "",
            valEu: formatCurrency(mainTotals?.vleraNeEuro ?? mainTotals?.VleraNeEuro ?? 0),
        },
    }), [mainTotals]);

    const detailsSummaryFooter = useMemo(() => ({
        labelField: "articleName",
        label: "Totali",
        values: {
            jan: formatCurrency(detailsTotals?.janar ?? detailsTotals?.Janar ?? 0),
            feb: formatCurrency(detailsTotals?.shkurt ?? detailsTotals?.Shkurt ?? 0),
            mar: formatCurrency(detailsTotals?.mars ?? detailsTotals?.Mars ?? 0),
            apr: formatCurrency(detailsTotals?.prill ?? detailsTotals?.Prill ?? 0),
            may: formatCurrency(detailsTotals?.maj ?? detailsTotals?.Maj ?? 0),
            jun: formatCurrency(detailsTotals?.qershor ?? detailsTotals?.Qershor ?? 0),
            jul: formatCurrency(detailsTotals?.korrik ?? detailsTotals?.Korrik ?? 0),
            aug: formatCurrency(detailsTotals?.gusht ?? detailsTotals?.Gusht ?? 0),
            sep: formatCurrency(detailsTotals?.shtator ?? detailsTotals?.Shtator ?? 0),
            oct: formatCurrency(detailsTotals?.tetor ?? detailsTotals?.Tetor ?? 0),
            nov: formatCurrency(detailsTotals?.nentor ?? detailsTotals?.Nentor ?? 0),
            dec: formatCurrency(detailsTotals?.dhjetor ?? detailsTotals?.Dhjetor ?? 0),
            tot: formatCurrency(detailsTotals?.total ?? detailsTotals?.Total ?? 0),
            avg: "",
            valEu: formatCurrency(detailsTotals?.vleraNeEuro ?? detailsTotals?.VleraNeEuro ?? 0),
        },
    }), [detailsTotals]);

    const monthNames = [
        "Janar", "Shkurt", "Mars", "Prill", "Maj", "Qershor",
        "Korrik", "Gusht", "Shtator", "Tetor", "Nëntor", "Dhjetor"
    ];

    function getMonthName(monthNumber) {
        return monthNames[monthNumber - 1] || "";
    }

    const monthlyChartRows = useMemo(() => {
        return (articleDetailsChart || []).map((item) => ({
            ...item,
            monthLabel: getMonthName(item.MonthNumber),
        }));
    }, [articleDetailsChart]);

    const fetchArticleDetails = async (idNumber, fiscalY) => {
        setDetailsLoading(true);
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.detailsV2,
                ARTICLE_CARD_ENDPOINTS.detailsLegacy,
                buildGridParams(
                    detailsPaginationModel,
                    detailsSortModel,
                    detailsFilterModel,
                    detailsQuickFilter,
                    { articleID: idNumber, fiscalYear: fiscalY }
                )
            );

            const normalized = normalizePagedPayload(response.data);
            setArticleDetails(normalized.items);
            setDetailsRowCount(Number(normalized.total ?? normalized.items.length));
            setDetailsTotals(normalized.totals || {});
            setArticleName(normalized.items.length > 0 ? normalized.items[0].ArticleName : "");
            fetchArticleDetailsForChart(idNumber, fiscalY);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        } finally {
            setDetailsLoading(false);
        }
    };

    const fetchArticleDetailsForChart = async (idNumber, fiscalY) => {
        try {
            const response = await requestWithFallback(
                ARTICLE_CARD_ENDPOINTS.detailsChartV2,
                ARTICLE_CARD_ENDPOINTS.detailsChartLegacy,
                { articleID: idNumber, fiscalYear: fiscalY }
            );

            setArticleDetailsChart(Array.isArray(response.data?.MonthlyData) ? response.data.MonthlyData : []);
        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const fetchAllSyntheticRows = async () => {
        const totalRowsToExport = Math.max(Number(mainRowCount) || 0, 1);
        const response = await requestWithFallback(
            ARTICLE_CARD_ENDPOINTS.syntheticV2,
            ARTICLE_CARD_ENDPOINTS.syntheticLegacy,
            buildGridParams(
                { page: 0, pageSize: totalRowsToExport },
                mainSortModel,
                mainFilterModel,
                mainQuickFilter,
            )
        );

        const normalized = normalizePagedPayload(response.data);
        return normalized.items;
    };

    const fetchAllDetailRows = async () => {
        if (!selectedArticleId) {
            return [];
        }

        const totalRowsToExport = Math.max(Number(detailsRowCount) || 0, 1);
        const response = await requestWithFallback(
            ARTICLE_CARD_ENDPOINTS.detailsV2,
            ARTICLE_CARD_ENDPOINTS.detailsLegacy,
            buildGridParams(
                { page: 0, pageSize: totalRowsToExport },
                detailsSortModel,
                detailsFilterModel,
                detailsQuickFilter,
                { articleID: selectedArticleId, fiscalYear }
            )
        );

        const normalized = normalizePagedPayload(response.data);
        return normalized.items;
    };

    const showDetails = () => {
        setShowAllAccounts(false);
        setShowDetailsAccounts(true);
    }

    const showAllAccounts = () => {
        setShowAllAccounts(true);
        setShowDetailsAccounts(false);
        setArticleDetails([]);
        setArticleDetailsChart([]);
        setArticleName("");
        setSelectedArticleId(null);
        setDetailsRowCount(0);
        setDetailsTotals({});
    }

    useEffect(() => {
        fetchMaterialSintetikData();
    }, [fiscalYear, mainPaginationModel, mainSortModel, mainFilterModel, mainQuickFilter]);

    useEffect(() => {
        fetchCompaniesData();
    }, [fiscalYear]);

    useEffect(() => {
        if (!selectedArticleId) {
            return;
        }

        fetchArticleDetails(selectedArticleId, fiscalYear);
    }, [selectedArticleId, fiscalYear, detailsPaginationModel, detailsSortModel, detailsFilterModel, detailsQuickFilter]);

    const openArticleDetails = (row) => {
        const articleId = row?.articleID || row?.ArticleID;
        if (!articleId) {
            return;
        }

        setDetailsPaginationModel({ page: 0, pageSize: detailsPaginationModel.pageSize || 50 });
        setDetailsSortModel([]);
        setDetailsFilterModel({ items: [] });
        setDetailsQuickFilter([]);
        setSelectedArticleId(articleId);
        showDetails();
    };

    return (
        <div className="Crud__Container ARP__page KAP__page">
            {showallAccounts && (
                <div className="ARP__content">
                    <CrudPageHeader
                        icon={FaChartLine}
                        title="Kartela sintetike e artikujve"
                        description="Forma është e përshtatur për kontroll, filtrim dhe analizë profesionale të lëvizjeve mujore sipas artikullit në vitin fiskal aktiv."
                        className="ARP__header"
                    />

                    <div className="ARP__reportGrid">
                        <ReusableAccountBooksGrid
                            columns={syntheticColumns}
                            rows={rows}
                            loading={mainLoading}
                            rowCount={mainRowCount}
                            height={650}
                            paginationModel={mainPaginationModel}
                            setPaginationModel={setMainPaginationModel}
                            sortModel={mainSortModel}
                            setSortModel={setMainSortModel}
                            filterModel={mainFilterModel}
                            setFilterModel={setMainFilterModel}
                            quickFilter={mainQuickFilter}
                            setQuickFilter={setMainQuickFilter}
                            showViewAction
                            showEditAction={false}
                            showDeleteAction={false}
                            showExportButton={false}
                            summaryFooter={summaryFooter}
                            onView={openArticleDetails}
                            toolbarActions={(
                                <>
                                    <AddActionsButton
                                        text="Print"
                                        onClick={() => printCardPdf("synthetic")}
                                        icon={<AiOutlinePrinter />}
                                        className="RSDG__toolButton ARP__gridActionBtn--print"
                                    />
                                    <AddActionsButton
                                        text="PDF"
                                        onClick={() => exportCardPdf("synthetic")}
                                        icon={<AiOutlineFilePdf />}
                                        className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                    />
                                    <ExcelFormat
                                        data={data}
                                        fileName={`Kartela sintetike e artikujve`}
                                        columns={sinteticArticleColumns}
                                        columnMappings={sinteticArticleMappings}
                                        buttonText="XLSX"
                                    />
                                </>
                            )}
                        />
                    </div>
                </div>
            )}

            {showDetailAccounts && (
                <div className="ARP__content">
                    <CrudPageHeader
                        icon={FaChartLine}
                        title={`Historiku i artikullit: ${articleName}`}
                        description="Forma është e përshtatur për kontroll, filtrim dhe analizë profesionale të detajeve sipas klientit dhe trendit mujor."
                        className="ARP__header"
                        action={(
                            <AddActionsButton
                                text="Mbyll"
                                onClick={showAllAccounts}
                                icon={<AiOutlineClose />}
                                className="RSDG__toolButton ARP__backBtn"
                            />
                        )}
                    />

                    <div className="KAP__chartCard ARP__reportGrid">
                        <div className="KAP__chartHead">
                            <h4>Trend mujor i artikullit</h4>
                            <p>Sasia dhe vlera mujore sipas vitit fiskal aktiv.</p>
                        </div>

                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                width={500}
                                height={500}
                                data={monthlyChartRows}

                            >
                                <CartesianGrid strokeDasharray="5 5" />
                                <XAxis dataKey="monthLabel" />
                                <YAxis />
                                <Tooltip
                                    labelFormatter={(label) => `Muaji: ${label}`}
                                />
                                <Legend />
                                <Bar dataKey="MonthQty" fill="#284945" name="Sasia" barSize={15} />
                                <Bar dataKey="MonthValue" fill="#82ca9d" name="Vlera" barSize={15} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="ARP__reportGrid ARP__reportGrid--details">
                        <ReusableAccountBooksGrid
                            columns={detailColumns}
                            rows={detailsRows}
                            loading={detailsLoading}
                            rowCount={detailsRowCount}
                            height={650}
                            paginationModel={detailsPaginationModel}
                            setPaginationModel={setDetailsPaginationModel}
                            sortModel={detailsSortModel}
                            setSortModel={setDetailsSortModel}
                            filterModel={detailsFilterModel}
                            setFilterModel={setDetailsFilterModel}
                            quickFilter={detailsQuickFilter}
                            setQuickFilter={setDetailsQuickFilter}
                            showViewAction={false}
                            showEditAction={false}
                            showDeleteAction={false}
                            showExportButton={false}
                            summaryFooter={detailsSummaryFooter}
                            toolbarActions={(
                                <>
                                    <AddActionsButton
                                        text="Print"
                                        onClick={() => printCardPdf("details")}
                                        icon={<AiOutlinePrinter />}
                                        className="RSDG__toolButton ARP__gridActionBtn--print"
                                    />
                                    <AddActionsButton
                                        text="PDF"
                                        onClick={() => exportCardPdf("details")}
                                        icon={<AiOutlineFilePdf />}
                                        className="RSDG__toolButton ARP__gridActionBtn--pdf"
                                    />
                                    <ExcelFormat
                                        data={articleDetails}
                                        fileName={`${articleName}-Detale`}
                                        columns={ArticleDetailsColumns}
                                        columnMappings={ArticleDetailsMappings}
                                        buttonText="XLSX"
                                    />
                                </>
                            )}
                        />
                    </div>
                </div>

            )}
        </div>
    )
}
